import { USER_TYPE } from "@mozioinc/query";
import { toAnalyticsFormat } from "@mozioinc/utils";
import i18next from "i18next";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { CHECKOUT_VERSION } from "../constants/checkout";
import { getEnvs } from "./env";
import { languageToOldFormat } from "./languages";
import { getWhiteLabel } from "./white-label";
import type { User } from "@mozioinc/query";

const { DEPLOY_ENV, MOZIO_PUBLIC_POSTHOG_KEY } = getEnvs();

const PRODUCT_NAME = "Transfer";

const POSTHOG_PROXY_HOST =
  DEPLOY_ENV === "development"
    ? "https://front-end-staging.mozio.com/v2/ingest"
    : "/v2/ingest";

// `satisfies` (not a `Record<string, …>` annotation) so the literal keys survive
// in `typeof FEATURE_FLAGS` — that's what makes `FeatureFlagKey` a union of the
// real flag names and lets `useFeatureFlag`/`getFeatureFlags` reject typos.
// Same shape as `apps/transfer-next/src/utils/posthog.ts`, so a flag's default
// is declared once instead of at every call site.
export const FEATURE_FLAGS = {
  // NOTE: Add new feature flags here following the same structure
  searchPageEnabled: {
    flag: "search-page-enabled",
    defaultValue: true,
  },
  showChat: {
    flag: "show-chat",
    defaultValue: false,
  },
  gratuityEnabled: {
    flag: "gratuity-enabled",
    defaultValue: false,
  },
} satisfies Record<string, { flag: string; defaultValue: boolean }>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

export type FeatureFlags = Record<FeatureFlagKey, boolean>;

export const setPostHog = () => {
  const refParam = new URLSearchParams(window.location.search).get("ref");

  if (refParam === "aws-synthetics-canaries") {
    return;
  }

  if (!MOZIO_PUBLIC_POSTHOG_KEY) {
    console.error(
      "Posthog integration is not configured properly so it will not be loaded.",
    );
    return;
  }

  const { id, name, currency, locale, partnerRef } = getWhiteLabel();

  posthog.init(MOZIO_PUBLIC_POSTHOG_KEY, {
    api_host: POSTHOG_PROXY_HOST,
    ui_host: "https://us.posthog.com",
    opt_out_capturing_by_default: localStorage.getItem("ph_enabled") !== "1",
    debug: DEPLOY_ENV === "development",
    disable_session_recording: true,
    disable_product_tours: false,
    loaded: (posthog) => {
      posthog.setPersonPropertiesForFlags({ $host: window.location.host });
      posthog.register({
        product: PRODUCT_NAME,
        app_version: APP_VERSION,
        white_label_name: name,
        white_label_id: id,
        partner_ref: partnerRef,
        //TODO: displayed_currency should be moved to header once we accept multiple currencies
        displayed_currency: currency,
        white_label_locale: locale || "en_US",
        displayed_language: toAnalyticsFormat(i18next.language),
      });
    },
    // NOTE: Do not bootstrap feature flags here. As of posthog-js 1.386.8, a
    // non-empty `bootstrap.featureFlags` sets the SDK's internal `_hasLoadedFlags`
    // to true during init, which suppresses the only first-load `/flags` fetch and
    // disables all server feature flags. `getFeatureFlags()` already falls back to
    // each flag's default, so the pre-load window is covered. See DEV-11695.
  });
};

let pendingIdentify: (() => void) | undefined;

// PostHog loads only on Osano consent, and posthog-js drops `identify` while
// uninitialized or opted out — so hold the latest call until consent is granted.
export const identifyPostHogUser = (user: User) => {
  const identify = () =>
    posthog.identify(String(user.id), {
      user_email: user.email,
      is_user_travel_agent: user.userType === USER_TYPE.TRAVEL_AGENT,
      user_default_currency: user.currency,
      user_default_language: user.locale,
      user_signup_up_date: user.signupDate,
      ref_partner_name: user.refPartnerName,
    });

  if (posthog.__loaded && posthog.has_opted_in_capturing()) {
    identify();
    return;
  }

  pendingIdentify = identify;
};

export const flushPendingPostHogIdentify = () => {
  pendingIdentify?.();
  pendingIdentify = undefined;
};

// `reset` also clears posthog-js's stored opt-in, which would silence capture
// until the next page load re-runs Osano — so restore it without an event.
export const resetPostHogUser = () => {
  pendingIdentify = undefined;
  if (!posthog.__loaded) return;

  const optedIn = posthog.has_opted_in_capturing();
  posthog.reset();
  if (optedIn) posthog.opt_in_capturing({ captureEventName: false });
};

// Imperative read — use inside event handlers / non-reactive logic. Falls back
// to the flag's registered `defaultValue` until PostHog resolves.
export const getFeatureFlags = (): FeatureFlags =>
  Object.fromEntries(
    Object.entries(FEATURE_FLAGS).map(([key, { flag, defaultValue }]) => [
      key,
      posthog.isFeatureEnabled(flag) ?? defaultValue,
    ]),
  ) as FeatureFlags;

// Reactive read — use at render time to gate UI. Subscribes to PostHog's
// `onFeatureFlags` rather than posthog-js/react's hook, because this app renders
// without a `PostHogProvider`. The default comes from the single `FEATURE_FLAGS`
// registry instead of a hardcoded `?? false` at every call site.
export const useFeatureFlag = (key: FeatureFlagKey): boolean => {
  const { flag, defaultValue } = FEATURE_FLAGS[key];
  const [enabled, setEnabled] = useState(
    () => posthog.isFeatureEnabled(flag) ?? defaultValue,
  );

  useEffect(
    () =>
      posthog.onFeatureFlags(() => {
        setEnabled(posthog.isFeatureEnabled(flag) ?? defaultValue);
      }),
    [flag, defaultValue],
  );

  return enabled;
};

// Same reactive pattern as `useFeatureFlag`, for a multivariate flag (e.g.
// "control"/"test") not covered by the boolean-only `FEATURE_FLAGS` registry.
// A synchronous `posthog.getFeatureFlag()` read at render time can return a
// stale variant if `/flags` resolves after mount with nothing else to trigger
// a re-render — this subscribes to `onFeatureFlags` so the variant updates
// once PostHog actually resolves it.
// `enabled: false` skips `getFeatureFlag()`, which is what records an
// experiment exposure, so users outside the experiment's audience never count.
export const useFeatureFlagVariant = <T extends string>(
  flag: string,
  defaultValue: T,
  { enabled = true }: { enabled?: boolean } = {},
): T => {
  // getFeatureFlag() can return a boolean (e.g. `false` pre-resolve, or for a
  // non-multivariate flag) — `??` alone won't catch that, so guard the type.
  const readVariant = () => {
    const value = posthog.getFeatureFlag(flag);
    return (typeof value === "string" ? value : defaultValue) as T;
  };

  const [variant, setVariant] = useState<T>(() =>
    enabled ? readVariant() : defaultValue,
  );

  useEffect(() => {
    if (!enabled) return;
    return posthog.onFeatureFlags(() => setVariant(readVariant()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag, defaultValue, enabled]);

  return enabled ? variant : defaultValue;
};

export const posthogCheckoutFormError = (
  fields: string[],
  totalPriceUsd?: number,
) => {
  posthog.capture("Checkout Form Error", {
    checkout_form_error_fields: fields,
    checkout_form_error_count: fields.length,
    total_amount_usd: totalPriceUsd,
  });
};

type PaymentError = {
  paymentErrorCode?: string;
  paymentErrorMessageDisplayed?: string;
  paymentErrorMessage?: string;
  totalAmountUsd?: string;
  paymentMethod?: string;
};

export const capturePaymentError = (error: PaymentError) => {
  posthog.capture("Payment Error", {
    payment_error_code: error.paymentErrorCode,
    payment_error_message_displayed: error.paymentErrorMessageDisplayed,
    payment_error_message: error.paymentErrorMessage,
    total_amount_usd: error.totalAmountUsd,
    payment_method: error.paymentMethod,
  });
};

export const posthogCheckoutFormSubmitted = (
  totalAmountUsd?: number,
  sessionId?: string,
) => {
  posthog.capture("Checkout Form Submitted", {
    total_amount_usd: totalAmountUsd,
    session_id: sessionId,
  });
};

export type BookingDialogCloseReason =
  | "poll_error"
  | "poll_price_status"
  | "stripe_redirect_failure"
  | "flight_select_not_available"
  | "confirm_payment_error"
  | "confirm_payment_rejected"
  | "stripe_submit_error"
  | "prepare_not_available"
  | "prepare_price_changed"
  | "prepare_unknown_response"
  | "prepare_error";

/** Temporary diagnostics: pinpoints which exit closes the booking dialog. */
export const posthogBookingDialogClosed = (
  reason: BookingDialogCloseReason,
  detail?: { stripeErrorType?: string; stripeErrorCode?: string },
) => {
  posthog.capture("Booking Dialog Closed", {
    reason,
    stripe_error_type: detail?.stripeErrorType,
    stripe_error_code: detail?.stripeErrorCode,
  });
};

export const posthogSmsAmenitySelected = (payload: {
  amount: string;
  isUserLoggedIn: boolean;
}) => {
  posthog.capture("SMS Amenity Selected", {
    amount: payload.amount,
    is_user_logged_in: payload.isUserLoggedIn,
    checkout_version: CHECKOUT_VERSION,
  });
};

export const posthogTrackingPageViewed = (
  trackingStatus: string,
  reservationType?: string,
) => {
  posthog.capture("Tracking Page Viewed", {
    tracking_status: trackingStatus,
    reservation_type: reservationType,
  });
};

export const posthogCheckoutPageViewed = (rideAvailable: boolean) => {
  posthog.startSessionRecording();
  posthog.capture("Checkout Page Viewed", {
    checkout_version: CHECKOUT_VERSION,
    ride_available: rideAvailable,
  });
};

export const captureCruiseShipSelectorViewed = (portName: string) => {
  posthog.capture("Cruise Ship Selector Viewed", {
    port_name: portName,
  });
};

export const captureCruiseShipSelectorInteracted = (
  firstFieldTouched: "cruise_line" | "ship_name",
  portName: string,
) => {
  posthog.capture("Cruise Ship Selector Interacted", {
    first_field_touched: firstFieldTouched,
    port_name: portName,
  });
};

type CruiseShipSelectorCompletedPayload = {
  cruiseLine: string;
  shipName: string;
  portName: string;
  autocompleteUsed: boolean;
  searchAttempts: number;
  timeToCompleteSeconds: number;
};

export const posthogBookingConfirmationTimedOut = (
  cartId: string,
  sessionId: string,
) => {
  posthog.capture("Booking Confirmation Timed Out", {
    cart_id: cartId,
    session_id: sessionId,
  });
};

export const posthogPaymentTimingChanged = (payload: {
  cartId: string;
  searchId: string;
  switchedToPayNow: boolean;
  totalAmountUsd?: number;
}) => {
  posthog.capture("Payment Timing Changed", {
    cart_id: payload.cartId,
    search_id: payload.searchId,
    switched_to_pay_now: payload.switchedToPayNow,
    total_amount_usd: payload.totalAmountUsd,
  });
};

export const posthogGratuityChanged = (payload: {
  cartId: string;
  searchId: string;
  gratuityPercent: number;
  totalAmountUsd?: number;
}) => {
  posthog.capture("Gratuity Changed", {
    cart_id: payload.cartId,
    search_id: payload.searchId,
    gratuity_percent: payload.gratuityPercent,
    total_amount_usd: payload.totalAmountUsd,
  });
};

export const captureCruiseShipSelectorCompleted = (
  payload: CruiseShipSelectorCompletedPayload,
) => {
  posthog.capture("Cruise Ship Selector Completed", {
    cruise_line: payload.cruiseLine,
    ship_name: payload.shipName,
    port_name: payload.portName,
    autocomplete_used: payload.autocompleteUsed,
    search_attempts: payload.searchAttempts,
    time_to_complete_seconds: payload.timeToCompleteSeconds,
  });
};

type LanguageChangedPayload = {
  /** The signed-in user's saved locale — `locale` on the user data payload. */
  userDataLocale: string;
  /** The locale the user just picked in the language dropdown. */
  newLocale: string;
};

/**
 * Fired when a signed-in user picks a different language in a language
 * dropdown. Both locales are normalized to the dashed API format (`en-us`) so
 * they stay comparable with each other and across apps.
 *
 * `send_instantly` because every dropdown navigates the page right after this
 * call, which would otherwise discard the still-queued batch.
 */
export const captureLanguageChanged = ({
  userDataLocale,
  newLocale,
}: LanguageChangedPayload) => {
  posthog.capture(
    "Language Changed",
    {
      user_data_locale: languageToOldFormat(userDataLocale),
      new_locale: languageToOldFormat(newLocale),
    },
    { send_instantly: true },
  );
};

export const SEARCH_PAGE_VARIANTS = [
  "v1",
  "v1-A",
  "v1-B",
  "v2",
  "v3",
  "control",
] as const;
export type SearchPageVariants = (typeof SEARCH_PAGE_VARIANTS)[number];
export const useSearchPageVariant = () =>
  useFeatureFlagVariant<SearchPageVariants>(
    "new-class-based-search-page",
    "control",
  );

// Resolves after `timeoutMs` even if flags never arrive: PostHog only starts
// once the visitor accepts analytics, and an ad blocker can stop `/flags`.
export const useFeatureFlagsLoaded = (timeoutMs: number): boolean => {
  const [loaded, setLoaded] = useState(
    () => posthog.featureFlags?.hasLoadedFlags ?? false,
  );

  useEffect(() => {
    if (loaded) return;

    const timeout = setTimeout(() => setLoaded(true), timeoutMs);
    const unsubscribe = posthog.onFeatureFlags(() => setLoaded(true));

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [loaded, timeoutMs]);

  return loaded;
};
